// Phase 2 (issue #84): converted to an ES module so it imports AudioModule from
// the real src module instead of reading the window.AudioModule bridge. index.html
// loads it as `<script type="module">`; like every module script it is deferred,
// so it still runs before DOMContentLoaded and its listeners are registered in time.
//
// Phase 3.11 (issue #84): renamed `.js` -> `.ts`. The only edit is the JSDoc
// `(window)` cast → `as`; the DOM/menu wiring carries no type surface to promote
// under the current non-strict config. The `../audio.js` import specifier is
// unchanged — Vite/tsc Bundler resolve it to audio.ts.
import { AudioModule } from '../audio.js';
import { getDifficultyConfig, storeDifficulty, type Difficulty } from '../difficulty.js';
import { consumeRunLaunch, initialRunDifficulty } from '../game/run-transition.js';
import { Sfx } from '../sfx.js';
import { buildDifficultyPicker as buildDifficultyPickerUI, type DifficultyPickerHandle } from './difficulty-picker.js';
import { isOnline, watchConnectivity } from '../offline/offline-state.js';
import { ensureOfflineBadge, setOfflineBadgeVisible } from '../offline/offline-ui.js';
import { initInstallPrompt, type InstallPromptController } from '../pwa/install-prompt.js';
import { closeOverlayFocus, focusGameCanvas, openOverlayFocus } from './accessibility.js';

(function () {
  let startGamePending = false;
  // The player's chosen difficulty tier, remembered across sessions in
  // localStorage. The picker writes it; the game reads the persisted value at run
  // start (src/snowglider.ts) so changing the pick then starting takes effect.
  let selectedDifficulty: Difficulty = initialRunDifficulty();
  let menuListeners: AbortController | null = null;
  // Handle to the shared picker widget; getSelectedDifficulty reads through it so the
  // live selection stays the single source of truth across rebuilds.
  let pickerHandle: DifficultyPickerHandle | null = null;
  // Monotonic token for refreshStartAccountUI: bumped on every call so a slow
  // in-flight leaderboard read can detect that a newer refresh superseded it
  // (e.g. the player logged out mid-read) and discard its now-stale result.
  let accountRefreshSeq = 0;
  let scrollBeforeAbout = 0;

  // Handle to the offline-mode badge + its connectivity subscription. The badge is
  // mounted hidden and only revealed when offline, so the online start screen is
  // unchanged; the unsubscribe keeps us teardown-clean if the menu is re-initialized.
  let offlineBadge: HTMLElement | null = null;
  let unwatchConnectivity: (() => void) | null = null;
  // The install-prompt controller; re-init disposes the prior one so we never stack
  // duplicate beforeinstallprompt listeners or leave a second chip behind.
  let installPrompt: InstallPromptController | null = null;

  // Mount the offline badge into the start container and keep its visibility in sync
  // with connectivity. Idempotent: re-init tears down the prior watcher first so we
  // never stack duplicate `online`/`offline` listeners.
  function setupOfflineBadge() {
    const container = document.getElementById('startStatus') ?? document.getElementById('startGameContainer');
    if (!container) return;
    offlineBadge = ensureOfflineBadge(container);
    setOfflineBadgeVisible(offlineBadge, !isOnline());
    if (unwatchConnectivity) unwatchConnectivity();
    unwatchConnectivity = watchConnectivity((online) => {
      setOfflineBadgeVisible(offlineBadge, !online);
      // Global-only affordances (sign-in hint + leaderboard preview) are decided from
      // Firebase-INITIALIZED state, which stays true after going offline; re-run the
      // refresh so they hide/show with connectivity, not just the badge (Codex #359).
      refreshStartAccountUI();
    });
  }

  // Wire the PWA install prompt (issue #358, PR 2). Additive and self-gating: the
  // chip only appears when the browser fires `beforeinstallprompt` and we're not
  // under automation/standalone. Dispose any prior controller first so a re-init
  // doesn't stack listeners.
  function setupInstallPrompt() {
    if (installPrompt) installPrompt.dispose();
    installPrompt = initInstallPrompt();
  }

  function addBuildBadge() {
    const buildMeta = document.querySelector('meta[name="build-id"]');
    const build = buildMeta instanceof HTMLMetaElement && buildMeta.content
      ? buildMeta.content
      : new Date().toISOString().slice(0, 16).replace('T', ' ');
    // Show the build version as an unobtrusive footer on the start screen rather
    // than as a pill on the primary "Start Game" CTA.
    const badge = document.getElementById('buildBadge');
    if (badge) {
      badge.textContent = `build ${build}`;
    }
  }

  function escapeHtml(value: string) {
    return String(value).replace(/[&<>"']/g, (ch) => {
      switch (ch) {
        case '&': return '&amp;';
        case '<': return '&lt;';
        case '>': return '&gt;';
        case '"': return '&quot;';
        default: return '&#39;';
      }
    });
  }

  // Populate the start-screen leaderboard preview and the optional sign-in hint.
  // Reuses the live AuthModule/ScoresModule (the in-game #authContainer keeps the
  // actual sign-in/profile state), so this only reads state — it never duplicates
  // the auth wiring. Safe to call repeatedly; degrades to hidden when Firestore is
  // unavailable (file:// / localhost / offline).
  function refreshStartAccountUI() {
    const auth = window.AuthModule;
    const scores = window.ScoresModule;
    const seq = ++accountRefreshSeq;

    const firebase = auth && typeof auth.isFirebaseAvailable === 'function'
      ? auth.isFirebaseAvailable()
      : { auth: false, firestore: false };
    const authState = auth && typeof auth.getAuthState === 'function'
      ? auth.getAuthState()
      : { user: null, isSignedIn: false };

    // Unranked tiers (Bunny/Black) have no global board: don't advertise the
    // sign-in benefit or fetch/render their (empty, write-denied) leaderboard.
    const ranked = getDifficultyConfig(selectedDifficulty).ranked;

    const hint = document.getElementById('startSignInHint');
    if (hint) {
      // Only show when signing in can actually deliver the advertised benefit:
      // a RANKED tier is selected, real auth AND Firestore are up (the
      // localhost/127.0.0.1 + file:// fallbacks skip Firestore, so leaderboard writes
      // are no-ops there), the player is signed out, AND we're online — offline, the
      // global leaderboard is unreachable so advertising it would be misleading.
      hint.style.display = (ranked && firebase.auth && firebase.firestore && !authState.isSignedIn && isOnline()) ? 'block' : 'none';
    }

    const lb = document.getElementById('startLeaderboard');
    if (!lb) {
      return;
    }
    // The Firestore rules only permit leaderboard get/list for signed-in users
    // (firestore.rules: `allow get, list: if isSignedIn()`). Reading while signed
    // out is denied — ScoresModule.getLeaderboard() swallows that into an empty
    // array (without disabling Firestore), which would otherwise render a
    // misleading "No times yet" preview and log a permission error on every
    // signed-out start screen. So only read once signed in; signed-out players get
    // the sign-in hint instead.
    // Also hide the preview when offline: getLeaderboard() would fail/return stale and
    // the offline badge already communicates the degraded state (Codex #359).
    if (!ranked || !authState.isSignedIn || !scores || typeof scores.getLeaderboard !== 'function' || !isOnline()) {
      lb.style.display = 'none';
      return;
    }

    // Show the board for the currently-selected tier (the picker is the tier toggle).
    Promise.resolve(scores.getLeaderboard(selectedDifficulty))
      .then((list) => {
        // Discard a superseded read: if another refresh ran after this one (e.g.
        // the player logged out while this signed-in read was in flight), don't
        // clobber its result — otherwise a slow read could re-show the leaderboard
        // on a now signed-out start screen (Firestore rules forbid that read).
        if (seq !== accountRefreshSeq) {
          return;
        }
        if (!Array.isArray(list) || list.length === 0) {
          // getLeaderboard() resolves [] for BOTH a genuinely empty board and a
          // swallowed read error (offline / transient Firestore failure), and
          // isFirebaseAvailable() can still report Firestore "available" — so the
          // two are indistinguishable here. Hide the preview rather than risk a
          // false "No times yet" during an outage.
          lb.style.display = 'none';
          return;
        }

        // authState + list are now typed via the AuthModuleApi/ScoresModuleApi boot
        // seams (types/globals.d.ts), so the .uid/.displayName/.time accesses below are
        // type-checked directly — no local `any` narrowing needed.
        const me = authState.user ?? null;
        const tierLabel = getDifficultyConfig(selectedDifficulty).label;
        let html = `<h3>🏆 ${tierLabel} Top Times</h3><table><tr><th>#</th><th>Player</th><th>Time</th></tr>`;
        list.slice(0, 5).forEach((entry, index) => {
          const isMe = me && entry.userId === me.uid;
          // Other players show their denormalized leaderboard-doc name (written by
          // ScoresModule.updateLeaderboard); entries from before the field existed
          // fall back to 'Anonymous'. Untrusted — escapeHtml below is mandatory.
          const name = isMe ? (me?.displayName || 'You') : (entry.displayName ?? 'Anonymous');
          html += `<tr class="${isMe ? 'current-user-score' : ''}"><td>${index + 1}</td><td>${escapeHtml(name)}</td><td>${Number(entry.time).toFixed(2)}s</td></tr>`;
        });
        html += '</table>';
        lb.innerHTML = html;
        lb.style.display = 'block';
      })
      .catch(() => {
        // Same staleness guard: don't let a superseded read's failure hide a board
        // that a newer refresh has since populated.
        if (seq !== accountRefreshSeq) {
          return;
        }
        lb.style.display = 'none';
      });
  }

  function startGame() {
    const startContainer = document.getElementById('startGameContainer');
    // Click/touch compatibility events and retained callbacks must not reset an
    // already-starting run or create a second loop/intro.
    if (!startContainer || startContainer.style.display === 'none') return false;
    // Unlock inside the original user gesture, even when scripts are still loading.
    // Audio readiness never blocks play; the browser still enforces autoplay policy.
    try {
      void AudioModule.resumeAudioContext().catch(() => {});
      AudioModule.playPreloadedAudio();
    } catch (error) {
      console.warn('Audio unavailable; continuing to play.', error);
    }
    Sfx.unlock();
    const gameCanvas = document.getElementById('gameCanvas');
    const canInitializeGame = typeof window.initializeGameWithAudio === 'function';

    if (!gameCanvas || !canInitializeGame) {
      startGamePending = true;
      setStartButtonWaiting(true);
      console.log("Start requested before game scripts finished loading; deferring until ready.");
      return false;
    }

    startGamePending = false;
    setStartButtonWaiting(false);

    if (startContainer) {
      startContainer.style.display = 'none';
      closeOverlayFocus(startContainer, false);
    }
    // Drop the start-screen account-control elevation now that the game is shown.
    document.body.classList.remove('start-screen-active');

    gameCanvas.style.display = 'block';
    focusGameCanvas();

    window.initializeGameWithAudio?.();
    return true;
  }

  function startPendingGameIfReady() {
    const gameCanvas = document.getElementById('gameCanvas');
    if (!gameCanvas || typeof window.initializeGameWithAudio !== 'function') {
      return false;
    }

    if (startGamePending) {
      startGamePending = false;
      return startGame();
    }

    setStartButtonWaiting(false);
    return true;
  }

  function setStartButtonWaiting(waiting: boolean) {
    const startButton = document.getElementById('startGameButton');
    if (!(startButton instanceof HTMLButtonElement)) {
      return;
    }

    startButton.disabled = waiting;
    startButton.textContent = waiting ? 'Preparing run…' : 'Start Game';
    if (waiting) {
      startButton.setAttribute('aria-busy', 'true');
    } else {
      startButton.removeAttribute('aria-busy');
    }
  }

  // Tier-aware jump copy (workstream A): a tier without the jump verb (Bunny,
  // ski.manualJump false) must not advertise it. Hides/shows the two jump rows in
  // the controls guide and the touch-note's ", and the center to jump" clause to
  // match the selected tier. The in-run touch REGION itself is gated separately at
  // run start (Controls.setJumpEnabled, called from the lifecycle reset).
  function updateJumpCopy() {
    const hasJump = getDifficultyConfig(selectedDifficulty).ski.manualJump;
    for (const id of ['jumpControlRow', 'hopControlRow', 'touchJumpNote']) {
      const el = document.getElementById(id);
      if (el) el.style.display = hasJump ? '' : 'none';
    }
  }

  // Build (or rebuild) the start-screen difficulty picker via the shared picker
  // widget (src/ui/difficulty-picker.ts) — the same factory builds the finish-screen
  // picker, so the DOM/ARIA/keyboard contract stays single-source. Pre-selects the
  // remembered tier; idempotent so a re-init (or the test harness) can rebuild cleanly.
  function buildDifficultyPicker() {
    const picker = document.getElementById('difficultyPicker');
    if (!picker) return;
    selectedDifficulty = initialRunDifficulty();
    pickerHandle = buildDifficultyPickerUI(picker, {
      initial: selectedDifficulty,
      heading: 'Difficulty',
      onChange: function (id) {
        selectedDifficulty = id;
        storeDifficulty(id);
        // Swap the start-screen leaderboard preview to the newly-selected tier's board.
        refreshStartAccountUI();
        // Keep the controls guide + touch-note honest for the newly-selected tier.
        updateJumpCopy();
      },
    });
    updateJumpCopy();
  }

  function getSelectedDifficulty(): Difficulty {
    return pickerHandle ? pickerHandle.getSelected() : selectedDifficulty;
  }

  function showAbout() {
    const aboutPanel = document.getElementById('aboutGamePanel');
    const card = aboutPanel?.closest<HTMLElement>('.start-dialog');
    if (card && !card.classList.contains('about-open')) scrollBeforeAbout = card.scrollTop;
    const controlsGuide = document.getElementById('controlsGuide');
    const startMenu = document.getElementById('startMenu');
    const keyboardHint = document.getElementById('keyboardHint');
    const picker = document.getElementById('difficultyPicker');

    if (aboutPanel) aboutPanel.style.display = 'block';
    if (controlsGuide) controlsGuide.style.display = 'none';
    if (startMenu) startMenu.style.display = 'none';
    if (keyboardHint) keyboardHint.style.display = 'none';
    // Hide the difficulty picker alongside the rest of the start controls so it
    // doesn't stay visible/clickable over the About panel.
    if (picker) picker.style.display = 'none';
    if (card) {
      card.classList.add('about-open');
      card.scrollTop = 0;
    }
    document.getElementById('aboutGameButton')?.setAttribute('aria-expanded', 'true');
    if (aboutPanel) openOverlayFocus(aboutPanel, {
      // Read from the top of the panel. The bottom Close action may need scrolling
      // on a short phone, so focusing it with preventScroll would hide that focus.
      initialFocus: aboutPanel, onEscape: hideAbout,
    });
  }

  function hideAbout() {
    const aboutPanel = document.getElementById('aboutGamePanel');
    const card = aboutPanel?.closest<HTMLElement>('.start-dialog');
    const controlsGuide = document.getElementById('controlsGuide');
    const startMenu = document.getElementById('startMenu');
    const keyboardHint = document.getElementById('keyboardHint');
    const picker = document.getElementById('difficultyPicker');

    if (aboutPanel) aboutPanel.style.display = 'none';
    if (controlsGuide) controlsGuide.style.display = '';
    if (startMenu) startMenu.style.display = '';
    if (keyboardHint) keyboardHint.style.display = '';
    if (picker) picker.style.display = ''; // restore the responsive CSS layout
    if (card) {
      card.classList.remove('about-open');
      card.scrollTop = scrollBeforeAbout;
    }
    document.getElementById('aboutGameButton')?.setAttribute('aria-expanded', 'false');
    if (aboutPanel) closeOverlayFocus(aboutPanel);
    document.getElementById('aboutGameButton')?.focus({ preventScroll: true });
  }

  function initializeStartMenu() {
    menuListeners?.abort();
    menuListeners = new window.AbortController();
    const listenerOptions = { signal: menuListeners.signal };
    addBuildBadge();
    setupOfflineBadge();
    setupInstallPrompt();
    buildDifficultyPicker();
    if (consumeRunLaunch()) {
      startGamePending = true;
      setStartButtonWaiting(true);
      document.getElementById('startGameContainer')?.classList.add('run-preparing');
    }
    const startContainer = document.getElementById('startGameContainer');
    const account = document.getElementById('authContainer');
    if (startContainer) openOverlayFocus(startContainer, {
      initialFocus: document.getElementById('startGameButton'),
      allowed: account ? [account] : [], modal: false,
    });
    // Surface the account/sign-in control above the start overlay while it's up.
    document.body.classList.add('start-screen-active');
    if (window.SnowGliderGameScriptsReady) {
      startPendingGameIfReady();
      refreshStartAccountUI();
    }

    const startGameButton = document.getElementById('startGameButton');
    if (startGameButton) {
      startGameButton.addEventListener('click', function () {
        startGame();
      }, listenerOptions);

      startGameButton.addEventListener('touchstart', function (e) {
        e.preventDefault();
        this.classList.add('touch-active');
      }, { ...listenerOptions, passive: false });

      startGameButton.addEventListener('touchend', function (e) {
        e.preventDefault();
        this.classList.remove('touch-active');
        startGame();
      }, { ...listenerOptions, passive: false });

      startGameButton.addEventListener('touchcancel', function () {
        this.classList.remove('touch-active');
      }, { ...listenerOptions, passive: true });
    }

    const aboutGameButton = document.getElementById('aboutGameButton');
    if (aboutGameButton) {
      aboutGameButton.addEventListener('click', showAbout, listenerOptions);
    }

    const closeAboutButton = document.getElementById('closeAboutButton');
    if (closeAboutButton) {
      closeAboutButton.addEventListener('click', hideAbout, listenerOptions);
    }

    document.addEventListener('keydown', function (event) {
      const startContainer = document.getElementById('startGameContainer');
      const aboutPanel = document.getElementById('aboutGamePanel');

      // Don't let Enter/Space start the run when focus is on an interactive start-screen
      // control whose own activation should NOT also launch the game: a difficulty option
      // (just selects the tier) or the PWA install chip (Install/dismiss — Codex #360, a
      // keyboard Enter on Install would otherwise both install AND start the run).
      const target = event.target as Element | null;
      if (target && typeof target.closest === 'function' && target.closest('button, a, summary, input, textarea, select, [role="radio"], [role="dialog"]')) {
        return;
      }

      if (startContainer &&
          startContainer.style.display !== 'none' &&
          aboutPanel &&
          aboutPanel.style.display !== 'block') {
        if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
          event.preventDefault();
          startGame();
        }
      } else if (aboutPanel && aboutPanel.style.display === 'block') {
        if (event.key === 'Escape') {
          const closeAboutButton = document.getElementById('closeAboutButton');
          if (closeAboutButton) closeAboutButton.click();
        }
      }
    }, listenerOptions);
  }

  document.addEventListener('DOMContentLoaded', initializeStartMenu);
  window.addEventListener('snowglider:game-scripts-ready', function () {
    startPendingGameIfReady();
    // Render the leaderboard/sign-in state now, and again shortly after to catch
    // Firebase auth + Firestore finishing their async init after scripts load.
    refreshStartAccountUI();
    setTimeout(refreshStartAccountUI, 1500);
  });
  // Re-render whenever auth state changes (auth.ts dispatches this on login/logout),
  // so signing in from the elevated start-screen control immediately swaps the
  // sign-in hint for the leaderboard instead of leaving stale state until reload.
  window.addEventListener('snowglider:auth-changed', refreshStartAccountUI);

  window.SnowGliderStartMenu = {
    startGame,
    showAbout,
    hideAbout,
    initializeStartMenu,
    startPendingGameIfReady,
    refreshStartAccountUI,
    buildDifficultyPicker,
    getSelectedDifficulty
  };
})();
